import {
  type FragmentType,
  useFragment as readFragment,
  TaxonomyNodeFieldsFragmentDoc,
  type TaxonomyTreeQuery,
  type TaxonomyType,
} from '@revelio/data-access';

import type { TreeData } from '../../components/tree/useTreeApi';

type TaxonomyNodeWithChildren = FragmentType<
  typeof TaxonomyNodeFieldsFragmentDoc
> & {
  children?: readonly TaxonomyNodeWithChildren[] | null;
};

export type TaxonomyLevelMetadata =
  TaxonomyTreeQuery['taxonomy']['levels'][number];

export type NormalizedTaxonomyTree = {
  levels: TaxonomyLevelMetadata[];
  nodes: TreeData[];
};

const EMPTY_NORMALIZED_TAXONOMY_TREE: NormalizedTaxonomyTree = {
  levels: [],
  nodes: [],
};

type TaxonomyTreePayload = NonNullable<TaxonomyTreeQuery['taxonomy']>;

const normalizedTaxonomyTreeByPayload = new WeakMap<
  TaxonomyTreePayload,
  NormalizedTaxonomyTree
>();

const capitalizeFirstCharacter = (value: string) => {
  if (value.length === 0) {
    return value;
  }

  return value.charAt(0).toUpperCase() + value.slice(1);
};

export const getTaxonomyCacheKey = ({
  taxonomyType,
  taxonomyId,
  taxonomyVersion,
  levels,
}: {
  taxonomyType: TaxonomyType;
  taxonomyId: number;
  taxonomyVersion?: string | null;
  levels?: readonly number[] | null;
}) => {
  const sortedLevels = [...(levels ?? [])].sort((a, b) => a - b).join(',');
  return [
    taxonomyType,
    taxonomyId,
    taxonomyVersion ?? 'latest',
    sortedLevels || 'all',
  ].join(':');
};

export const normalizeTaxonomyNodes = (
  nodes: readonly TaxonomyNodeWithChildren[],
  depth = 0
): TreeData[] => {
  return nodes
    .map((node): TreeData => {
      const nodeFields = readFragment(TaxonomyNodeFieldsFragmentDoc, node);
      const children =
        node.children && node.children.length > 0
          ? normalizeTaxonomyNodes(node.children, depth + 1)
          : undefined;
      return {
        id: String(nodeFields.id),
        name: nodeFields.name,
        nodeCount: nodeFields.count,
        depth,
        ...(nodeFields.titles != null
          ? { taxonomyTitles: nodeFields.titles }
          : {}),
        ...(children ? { children } : {}),
      };
    })
    .sort((a, b) => (b.nodeCount ?? 0) - (a.nodeCount ?? 0));
};

export const normalizeTaxonomyTree = (
  taxonomy: TaxonomyTreeQuery['taxonomy'] | null | undefined
): NormalizedTaxonomyTree => {
  if (!taxonomy) {
    return EMPTY_NORMALIZED_TAXONOMY_TREE;
  }

  const cached = normalizedTaxonomyTreeByPayload.get(taxonomy);
  if (cached !== undefined) {
    return cached;
  }

  const normalized: NormalizedTaxonomyTree = {
    levels: taxonomy.levels,
    nodes: normalizeTaxonomyNodes(taxonomy.nodes),
  };
  normalizedTaxonomyTreeByPayload.set(taxonomy, normalized);
  return normalized;
};

export const getTaxonomyBranchLabels = (
  levels: readonly TaxonomyLevelMetadata[]
) => {
  return [...levels]
    .sort((a, b) => a.id - b.id)
    .map((level) => ({
      label: capitalizeFirstCharacter(
        level.label ?? level.tag ?? `Level ${level.id}`
      ),
    }));
};
